import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { FileText } from "lucide-react";

import type { Document } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Document };

type DocumentCardProps = Document & BaseEntityCardProps;

// Helper function to get a friendly name for mime types
const getMimeTypeLabel = (mimeType: string) => {
  const mimeMap: Record<string, string> = {
    "application/vnd.google-apps.document": "Google Doc",
    "application/vnd.google-apps.spreadsheet": "Google Sheet",
    "application/vnd.google-apps.presentation": "Google Slides",
    "application/pdf": "PDF",
    "text/plain": "Text",
    "text/html": "HTML",
    "application/msword": "Word",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word",
    "application/vnd.ms-excel": "Excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "Excel",
    "application/vnd.ms-powerpoint": "PowerPoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "PowerPoint",
  };

  return mimeMap[mimeType] || mimeType;
};

// Avatar component for DocumentCard
export function DocumentCardAvatar({
  className,
  style,
}: Partial<DocumentCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <FileText className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function DocumentCard({
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  updated_at,
  ...documentProps
}: DocumentCardProps) {
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <DocumentCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={documentProps.platform}>
            {documentProps.title}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {documentProps.web_link && (
              <BaseEntityCardExternalLinkAction
                href={documentProps.web_link}
                tooltip="Open document"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardMetadata>
            {documentProps.mime_type && (
              <span>{getMimeTypeLabel(documentProps.mime_type)}</span>
            )}
            {updated_at && (
              <span>
                Updated{" "}
                {formatDistanceToNow(new Date(updated_at), {
                  addSuffix: true,
                })}
              </span>
            )}
          </BaseEntityCardMetadata>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function DocumentCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={className} style={style}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-2.5" />
        <Skeleton className="h-3 w-1/4 mb-1.5" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
